import React, { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import '../styles/library.css';

const PDF_TYPES = ['module', 'magic', 'item', 'bestiary'];
const MAX_SIZE = 5 * 1024 * 1024;
const db = firebase.firestore();

const UploadPdfModal = ({ user, onClose, onSuccess, userDocsCount = 0 }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('module');
  const [access, setAccess] = useState('public');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    db.collection('campaigns')
      .where('idUserDm', '==', user.uid)
      .where('active', '==', true)
      .get()
      .then(snap => setUserCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user]);

  const toggleCampaign = (id) => {
    setSelectedCampaigns(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error(i18next.t('library.upload.titleRequired')); return; }
    if (!file) { toast.error(i18next.t('library.upload.fileRequired')); return; }
    if (file.size > MAX_SIZE) { toast.error(i18next.t('library.upload.tooLarge')); return; }
    if (access === 'private' && selectedCampaigns.length === 0) {
      toast.error(i18next.t('library.upload.campaignRequired'));
      return;
    }

    setUploading(true);
    try {
      const fileName = `${user.uid}_${Date.now()}.pdf`;
      const ref = firebase.storage().ref(`library/${fileName}`);
      await ref.put(file, { contentType: 'application/pdf', contentDisposition: 'inline' });
      const url = await ref.getDownloadURL();

      const docData = {
        title: title.trim(),
        description: description.trim(),
        type,
        access,
        authorUid: user.uid,
        authorName: user.name || user.displayName || 'Anonyme',
        url,
        fileName,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        votes: {},
        reports: {},
        campaignIds: access === 'private' ? selectedCampaigns : [],
      };

      await db.collection('library').add(docData);
      toast.success(i18next.t('library.upload.success'));
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(i18next.t('library.upload.error'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="libraryModalOverlay" onClick={onClose}>
      <div className="libraryModal" onClick={e => e.stopPropagation()}>
        <button className="libraryModalClose" onClick={onClose}>×</button>
        <h2 className="libraryModalTitle">{i18next.t('library.contribute')}</h2>
        <p className="libraryUploadQuotaInfo">{i18next.t('library.upload.quotaInfo', { used: userDocsCount, max: 3 })}</p>

        <form onSubmit={handleSubmit} className="libraryUploadForm">
          <label className="libraryFormLabel">
            {i18next.t('library.upload.title')}
            <input
              type="text"
              className="libraryFormInput"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={i18next.t('library.upload.titlePlaceholder')}
              maxLength={80}
            />
          </label>

          <label className="libraryFormLabel">
            {i18next.t('library.upload.description')}
            <textarea
              className="libraryFormInput libraryFormTextarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={i18next.t('library.upload.descriptionPlaceholder')}
              maxLength={400}
              rows={3}
            />
          </label>

          <label className="libraryFormLabel">
            {i18next.t('library.upload.type')}
            <select
              className="libraryFormInput"
              value={type}
              onChange={e => setType(e.target.value)}
            >
              {PDF_TYPES.map(t => (
                <option key={t} value={t}>{i18next.t(`library.types.${t}`)}</option>
              ))}
            </select>
          </label>

          <label className="libraryFormLabel">
            {i18next.t('library.upload.access')}
            <select
              className="libraryFormInput"
              value={access}
              onChange={e => { setAccess(e.target.value); setSelectedCampaigns([]); }}
            >
              <option value="public">{i18next.t('library.upload.public')}</option>
              <option value="private">{i18next.t('library.upload.private')}</option>
            </select>
          </label>

          {access === 'private' && (
            <div className="libraryFormLabel">
              <span>{i18next.t('library.upload.selectCampaigns')}</span>
              {userCampaigns.length === 0 ? (
                <p className="libraryFormHint">{i18next.t('library.noCampaigns')}</p>
              ) : (
                <div className="libraryCampaignPicker">
                  {userCampaigns.map(c => (
                    <button
                      type="button"
                      key={c.id}
                      className={`libraryCampaignOption${selectedCampaigns.includes(c.id) ? ' selected' : ''}`}
                      onClick={() => toggleCampaign(c.id)}
                    >
                      {selectedCampaigns.includes(c.id) ? '✓ ' : ''}{c.name}
                    </button>
                  ))}
                </div>
              )}
              {selectedCampaigns.length === 0 && (
                <span className="libraryFormHint libraryFormHintError">
                  {i18next.t('library.upload.campaignRequired')}
                </span>
              )}
            </div>
          )}

          <label className="libraryFormLabel">
            {i18next.t('library.upload.file')}
            <div className="libraryFileRow">
              <label className="libraryFileLabel">
                <input
                  type="file"
                  accept=".pdf"
                  className="inputfile"
                  onChange={e => setFile(e.target.files[0])}
                />
                <span>{file ? file.name : i18next.t('library.upload.chooseFile')}</span>
              </label>
              {file && (
                <span className={`libraryFileSize${file.size > MAX_SIZE ? ' error' : ''}`}>
                  {(file.size / 1024 / 1024).toFixed(1)} Mo
                </span>
              )}
            </div>
            <span className="libraryFormHint">{i18next.t('library.upload.sizeHint')}</span>
          </label>

          <div className="libraryUploadActions">
            <button type="button" className="libraryModalCancelBtn" onClick={onClose}>
              {i18next.t('cancel')}
            </button>
            <button type="submit" className="libraryModalSubmitBtn" disabled={uploading}>
              {uploading ? i18next.t('library.upload.uploading') : i18next.t('library.upload.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadPdfModal;
